import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { PeopelsLogo } from "@/components/brand/PeopelsLogo";
import { supabase } from "@/integrations/supabase/client";
import { getHelperMetrics } from "@/lib/bookings.functions";
import {
  Loader2, CheckCircle2, ChevronRight, UserCircle, Briefcase, MapPin,
  ShieldCheck, CreditCard, Sparkles, PartyPopper, Clock, Star,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/helper/onboard")({
  head: () => ({ meta: [{ title: "Your onboarding — Helper · Peopels" }, { name: "robots", content: "noindex" }] }),
  component: OnboardPage,
});

type Metrics = Awaited<ReturnType<typeof getHelperMetrics>>;

const STEP_META: Record<string, { icon: typeof UserCircle; title: string; body: string; href?: string; ext?: boolean }> = {
  avatar:   { icon: UserCircle, title: "Add a profile photo",   body: "A clear headshot helps customers recognise and trust you.",  href: "/dashboard" },
  headline: { icon: Sparkles,    title: "Write a headline",      body: "One line on what you do best (max 80 chars).",       href: "/dashboard" },
  bio:      { icon: Briefcase,   title: "Write your About",      body: "Tell customers your experience, in 2–3 sentences.",  href: "/dashboard" },
  postcode: { icon: MapPin,      title: "Set your service area", body: "We'll only show you jobs within your radius.",       href: "/dashboard" },
  rate:     { icon: Briefcase,   title: "Set your hourly rate",  body: "Most Helpers start at £25–£35/hr.",                  href: "/dashboard" },
  id:       { icon: ShieldCheck, title: "Verify your ID",        body: "Reviewed by a real human within 24 hours.",          href: "/helper/kyc" },
  stripe:   { icon: CreditCard,  title: "Connect Stripe payouts",body: "Secure weekly payouts to your UK bank account.",     href: "/become-helper" },
};

function OnboardPage() {
  const navigate = useNavigate();
  const fetchMetrics = useServerFn(getHelperMetrics);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/helper/onboard" } }); return; }
      try { setMetrics(await fetchMetrics() as Metrics); } finally { setLoading(false); }
    })();
  }, [navigate, fetchMetrics]);

  if (loading || !metrics) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <Header />
        <main className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></main>
        <Footer />
      </div>
    );
  }

  const percent = metrics.checklist.percent;
  const done = metrics.checklist.items.filter((c) => c.done).length;
  const total = metrics.checklist.items.length;
  const nextStep = metrics.checklist.items.find((c) => !c.done);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <PeopelsLogo variant="primary" height={52} className="mb-6" />
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Your onboarding
        </div>
        <h1 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
          {percent === 100 ? "You're ready to earn! 🎉" : "Finish setting up your Helper profile"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">
          {percent === 100
            ? "Your profile is 100% complete and visible in customer search."
            : "A complete profile helps customers choose you with confidence."}
        </p>

        {/* Progress hero */}
        <section className="mt-6 overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Profile completion</p>
              <p className="text-xs text-muted-foreground">{done} of {total} steps complete</p>
            </div>
            <span className="font-display text-3xl font-extrabold tabular-nums sm:text-4xl">{percent}%</span>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>

          {nextStep && (
            <div className="mt-5 rounded-xl bg-accent/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Next up</p>
              <p className="mt-1 font-display text-lg font-semibold">{STEP_META[nextStep.key]?.title ?? nextStep.label}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">{STEP_META[nextStep.key]?.body}</p>
              <Button asChild size="sm" className="mt-3 rounded-full">
                <Link to={STEP_META[nextStep.key]?.href ?? "/dashboard"}>
                  Continue <ChevronRight className="ml-0.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </section>

        {/* Quick wizard CTA */}
        {percent < 100 && (
          <section className="mt-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-emerald-500/5 p-4 sm:p-5">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <div className="min-w-0">
                <p className="font-display text-base font-semibold sm:text-lg">Fast-track setup</p>
                <p className="text-xs text-muted-foreground sm:text-sm">3-minute wizard: services, availability &amp; AI-guided pricing.</p>
              </div>
              <Button asChild size="sm" className="rounded-full">
                <Link to="/helper/onboard/wizard">Start <ChevronRight className="ml-0.5 h-4 w-4" /></Link>
              </Button>
            </div>
          </section>
        )}

        {/* Celebration */}
        {percent === 100 && <CompletionCelebration />}

        {/* Checklist */}
        <section className="mt-8">
          <h2 className="mb-3 font-display text-lg font-semibold sm:text-xl">All steps</h2>
          <ul className="space-y-2">
            {metrics.checklist.items.map((c) => {
              const meta = STEP_META[c.key];
              const Icon = meta?.icon ?? CheckCircle2;
              return (
                <li
                  key={c.key}
                  className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border bg-card p-4 shadow-card transition-colors sm:p-5 ${
                    c.done ? "border-emerald-200 dark:border-emerald-900/40" : "border-border"
                  }`}
                >
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${c.done ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-accent text-primary"}`}>
                    {c.done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </span>
                  <div className="min-w-0">
                    <p className={`truncate font-semibold ${c.done ? "text-muted-foreground line-through" : ""}`}>
                      {meta?.title ?? c.label}
                    </p>
                    {!c.done && meta?.body && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{meta.body}</p>
                    )}
                  </div>
                  {!c.done && meta?.href && (
                    <Button asChild size="sm" variant="outline" className="shrink-0 rounded-full">
                      <Link to={meta.href}>Do it <ChevronRight className="ml-0.5 h-3 w-3" /></Link>
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* Reassurance footer */}
        <section className="mt-8 grid gap-3 sm:grid-cols-3">
          <Reassure icon={Clock} title="Reviewed in 24h" body="ID checks reviewed by a real human, weekdays." />
          <Reassure icon={ShieldCheck} title="Escrow payments" body="Customers pay into Stripe escrow — released to you when the job is done." />
          <Reassure icon={Star} title="Build your rep" body="Reviews and badges unlock more bookings." />
        </section>
      </main>
      <Footer />
    </div>
  );
}

function CompletionCelebration() {
  return (
    <section className="mt-6 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-5 shadow-card sm:p-6 dark:border-emerald-900/40 dark:from-emerald-950/30 dark:via-card dark:to-card">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
          <PartyPopper className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-xl font-semibold sm:text-2xl">You're live on Peopels</h2>
          <p className="mt-1 text-sm text-muted-foreground">You'll see job offers in your dashboard as they come in. Reply quickly to give yourself the best chance of winning the booking.</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button asChild className="rounded-full"><Link to="/helper/offers">See incoming jobs</Link></Button>
        <Button asChild variant="outline" className="rounded-full"><Link to="/dashboard">Edit profile</Link></Button>
      </div>
    </section>
  );
}

function Reassure({ icon: Icon, title, body }: { icon: typeof Clock; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <Icon className="h-4 w-4 text-primary" />
      <p className="mt-2 font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground">{body}</p>
    </div>
  );
}
